// ═══════════════════════════════════════════════════════════════════════════════
// UnifiedLogin — LEAN-04: ONE login for all roles, replacing the 3 per-portal
// login screens (admin / clinic / anaesthetist) and the launcher.
//
// Authenticates against /api/auth/login (the server returns the user's role),
// runs the shared post-login setup (brute-force reset, session, at-rest
// encryption, audit) once, and hands { token, user, session } to App, which
// routes to the matching portal by role. Each portal then renders pre-authed.
// ═══════════════════════════════════════════════════════════════════════════════
import React, { useState } from 'react';
import { SessionManager } from './SessionManager.js';
import { AuditLogger } from './AuditLogger.js';
import { BruteForceGuard, Encryption } from './Security.js';

export function UnifiedLogin({ onSuccess }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [totp, setTotp] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    setErr('');
    const guard = BruteForceGuard.check('login', u);
    if (!guard.allowed) { setErr(guard.message); return; }
    setBusy(true);
    try {
      const resp = await fetch('https://sykessedation.co.uk/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: u, password: p, totp_code: totp || undefined }),
      });
      const data = await resp.json();
      if (resp.ok && data.mfa_required) { setMfaRequired(true); setBusy(false); return; }
      if (resp.ok && data.token && data.user) {
        const role = data.user.role;
        if (!['admin', 'clinic', 'anaesthetist'].includes(role)) {
          setErr('This account has no portal access.'); setBusy(false); return;
        }
        BruteForceGuard.reset('login', u);
        const session = await SessionManager.createSession(role, u);
        // SEC: at-rest encryption keyed per (user, token) — same scheme the portals used
        Encryption.init((u || role) + ':' + data.token.slice(0, 16)).catch(() => {});
        AuditLogger.setAuthToken(data.token);
        AuditLogger.log('login', role, u, `Unified login (API auth${mfaRequired ? ' + MFA' : ''}) — role ${role}`);
        onSuccess({ token: data.token, user: data.user, session });
        return;
      }
      if (mfaRequired) { setErr(data.error || 'Invalid verification code.'); }
      else {
        BruteForceGuard.recordFailure('login', u);
        const remaining = BruteForceGuard.remaining('login', u);
        setErr((data.error || 'Invalid credentials') + `. ${remaining} attempt${remaining !== 1 ? 's' : ''} remaining.`);
      }
    } catch (e) {
      setErr('Cannot reach the server.');
    }
    setBusy(false);
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg,#0c1222,#1e293b,#0c4a6e)', fontFamily: '"DM Sans",-apple-system,sans-serif' }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <div style={{ background: '#fff', borderRadius: 20, padding: 44, width: 380, boxShadow: '0 30px 60px rgba(0,0,0,.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#0f172a', letterSpacing: 2 }}>SYKES</div>
          <div style={{ fontSize: 11, color: '#64748b', letterSpacing: 3, marginTop: 4 }}>ANAESTHETICS — PORTAL</div>
        </div>
        {!mfaRequired ? <>
          <input placeholder="Username" value={u} onChange={e => setU(e.target.value)} style={inp} />
          <input placeholder="Password" type="password" value={p} onChange={e => setP(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') submit(); }} style={{ ...inp, marginBottom: 16 }} />
          <button onClick={submit} disabled={busy} style={btn(busy)}>{busy ? 'Signing in…' : 'Sign In'}</button>
        </> : <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>🔐</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Two-Factor Authentication</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>Enter the 6-digit code from your authenticator</div>
          </div>
          <input placeholder="000000" value={totp} onChange={e => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={e => { if (e.key === 'Enter' && totp.length === 6) submit(); }} style={{ ...inp, marginBottom: 16, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 }} maxLength={6} autoFocus />
          <button onClick={submit} disabled={totp.length !== 6 || busy} style={btn(totp.length !== 6 || busy)}>Verify</button>
          <button onClick={() => { setMfaRequired(false); setTotp(''); setErr(''); }} style={{ width: '100%', padding: 10, background: 'transparent', color: '#64748b', border: 'none', fontSize: 12, cursor: 'pointer', marginTop: 8 }}>← Back</button>
        </>}
        {err && <div style={{ textAlign: 'center', marginTop: 14, fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: '8px 12px', borderRadius: 8, border: '1px solid #fecaca' }}>{err}</div>}
        <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: '#94a3b8' }}>Secure authentication required</div>
      </div>
    </div>
  );
}

const inp = { width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 14, marginBottom: 12, boxSizing: 'border-box' };
const btn = (disabled) => ({ width: '100%', padding: 13, background: disabled ? '#94a3b8' : 'linear-gradient(135deg,#0f172a,#1e3a5f)', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer', letterSpacing: .5 });
